import { useQuery } from "@apollo/client";
import {
  Issue,
  ProgramArea,
  SubmissionStatus,
  SubmissionStepInfo,
  SubmissionStepStatus,
  SubmissionStepType,
} from "generated-graphql/graphql";
import { useMemo } from "react";
import { isContactIssue, useFacility } from "routes/Customer/Facility/useFacility";
import {
  GET_SUBMISSION_ISSUES,
  GET_SUBMISSION_RCRA_NOTIFICATION_ISSUES,
} from "routes/Customer/Waste/Submissions/Stepper/queries";
import { isRcraContactIssue } from "util/facilityContacts";

/**
 * EPCRA-classified state-field keys that are surfaced on the otherwise RCRA-only
 * State Info step for WA facilities. WA's UBI (Unified Business Identifier) is an
 * EPCRA field in the compliance engine, but it's required on the 8700-12 filing,
 * so the stepper shows it alongside the RCRA fields for WA — the user neither
 * knows nor cares that it's classified as EPCRA. Shared between the State Info
 * step (which renders/saves it) and the issue bucketing here (so a missing UBI
 * flags the step) to keep the two in sync.
 */
export const WA_EXTRA_STATE_FIELD_KEYS = ["ubi"];

/** State code whose facilities get the extra (EPCRA) state fields above. */
export const WA_STATE_CODE = "WA";

/**
 * Per-step display state for the waste submission stepper. Local to the waste
 * stepper — do NOT reuse TierII's ReportStepState; the underlying status enum
 * and rules differ.
 */
export enum SubmissionStepState {
  NotStarted = "not_started",
  InProgress = "in_progress",
  Done = "done",
  Invalid = "invalid",
}

/**
 * Pure derivation of a step's display state from its backend status plus
 * whether the compliance engine reported issues for it.
 *
 * - hasIssues → Invalid (overrides status)
 * - COMPLETED → Done
 * - IN_PROGRESS → InProgress
 * - NOT_STARTED / undefined → NotStarted
 */
export function getStepStatus({
  stepStatus,
  hasIssues,
}: {
  stepStatus?: SubmissionStepStatus;
  hasIssues: boolean;
}): SubmissionStepState {
  if (hasIssues) {
    return SubmissionStepState.Invalid;
  }
  if (stepStatus === SubmissionStepStatus.Completed) {
    return SubmissionStepState.Done;
  }
  if (stepStatus === SubmissionStepStatus.InProgress) {
    return SubmissionStepState.InProgress;
  }
  return SubmissionStepState.NotStarted;
}

function isRcraProfileIssue(issue: Issue): boolean {
  return (
    issue.modelName === "FacilityRcraProfile" ||
    (issue.modelName === "Facility" && issue.key === "facilityRcraProfile")
  );
}

function isStateFieldIssue(issue: Issue): boolean {
  return issue.modelName === "FacilityStateField";
}

/**
 * The submission-scoped 8700-12 models edited on the Filing Notifications step:
 * §13 episodic event (+ child waste lines), §15 site closure, and §16 HSM
 * notification (+ child activities). Parent and child models are both included
 * so an issue on any of them flags the step.
 *
 * Compliance-engine rules DO emit issues for these models, but because the
 * models are submission-scoped the issues flow through
 * `Submission.rcraNotificationIssues` (merged in as `filingNotificationIssues`)
 * rather than `Facility.issues`. This facility-issue filter therefore never
 * matches in practice; it is kept as a defensive no-op so that if any of these
 * issues ever did surface through `Facility.issues` they would still bucket to
 * the Filing Notifications step.
 */
const FILING_NOTIFICATION_MODEL_NAMES = new Set([
  "SubmissionRcraEpisodicEvent",
  "SubmissionRcraEpisodicWaste",
  "SubmissionRcraSiteClosure",
  "SubmissionRcraHsmNotification",
  "SubmissionRcraHsmActivity",
]);

function isFilingNotificationIssue(issue: Issue): boolean {
  return FILING_NOTIFICATION_MODEL_NAMES.has(issue.modelName);
}

/**
 * Buckets the facility-level compliance issues by submission step.
 *
 * The stepper route has no `:facilityId` path param, so unlike the param-based
 * facility issue hooks we must source issues from `useFacility(facilityId)`
 * explicitly. The per-step predicates mirror `useRcraProfileIssues`,
 * `useContactIssues`, and `useStateInformationIssues` from `useFacility`.
 *
 * Steps that are review-only or fully derived (generator status, waste streams,
 * overview, review) have no directly attributable issues.
 *
 * `wasteActivityIssues` are sourced separately (compliance-engine validation of
 * the submission's saved Waste Activity row, not facility issues) and bucketed
 * onto the Waste Activity step so it derives Invalid/Done like the others.
 */
export function useSubmissionIssuesGroupedByStep(
  facilityId: string,
  wasteActivityIssues: Issue[] = [],
  submissionId?: string
): Partial<Record<SubmissionStepType, Issue[]>> {
  const { data } = useFacility(facilityId);
  const issues = data?.facility?.issues;
  const stateFields = data?.facility?.stateFields;
  const facilityState = data?.facility?.state;

  // §13/§15/§16 are submission-scoped, so their issues don't reach
  // `Facility.issues`; fetch them from the submission (server-validated against
  // the persisted rows + the facility's federal generator status). §14 still
  // comes from `facilityIssues` below.
  const { data: notificationData } = useQuery(GET_SUBMISSION_RCRA_NOTIFICATION_ISSUES, {
    variables: { id: submissionId ?? "" },
    skip: !submissionId,
  });
  const notificationIssues = notificationData?.submission?.rcraNotificationIssues;

  // Document forms have none of the facility-issue steps above, so without this
  // every step reads clean while Review & sign still refuses to send.
  const { data: submissionIssuesData } = useQuery(GET_SUBMISSION_ISSUES, {
    variables: { id: submissionId ?? "" },
    skip: !submissionId,
  });
  const submissionIssues = submissionIssuesData?.submission?.issues;

  return useMemo(() => {
    const facilityIssues = issues ?? [];
    const filingNotificationIssues = notificationIssues ?? [];
    // The waste stepper is RCRA-only, with one exception: WA facilities also show
    // (and so can be flagged on) the EPCRA UBI field — see
    // WA_EXTRA_STATE_FIELD_KEYS. State-field issues are keyed by the field `key`,
    // so an issue counts for this step only when its key belongs to an RCRA
    // (waste) field or one of those WA extras.
    const isWa = facilityState === WA_STATE_CODE;
    const stepStateFieldKeys = new Set(
      (stateFields ?? [])
        .filter(
          (f) => f.type === ProgramArea.Rcra || (isWa && WA_EXTRA_STATE_FIELD_KEYS.includes(f.key))
        )
        .map((f) => f.key)
    );
    return {
      [SubmissionStepType.FacilityProfile]: facilityIssues.filter(isRcraProfileIssue),
      // Mirrors `ContactsStep`/`WasteContacts`: a contact issue counts only when
      // it is also RCRA-scoped (`isRcraContactIssue`), excluding Tier II/EPCRA
      // contact gaps that the step itself never surfaces.
      [SubmissionStepType.Contacts]: facilityIssues.filter(
        (issue) => isContactIssue(issue) && isRcraContactIssue(issue)
      ),
      [SubmissionStepType.StateFields]: facilityIssues.filter(
        (issue) =>
          isStateFieldIssue(issue) && issue.key != null && stepStateFieldKeys.has(issue.key)
      ),
      [SubmissionStepType.RcraWasteActivity]: wasteActivityIssues,
      // Filing Notifications owns §13/§14/§15/§16, all submission-scoped now.
      // §13/§15/§16 (episodic, closure, HSM) and §14 LQG consolidation
      // (`modelName === "SubmissionRcraConsolidation"`) all come from
      // `Submission.rcraNotificationIssues`. The `isFilingNotificationIssue`
      // facility-issue filter is kept as a forward-looking no-op (see its note).
      [SubmissionStepType.FilingNotifications]: [
        ...facilityIssues.filter(isFilingNotificationIssue),
        ...filingNotificationIssues,
      ],
      // Document-form steps. The attached filing is Prepare Document's;
      // everything else lands on Reviewer Preparation, which is where the
      // submission-level fields (method, facilities, due date) are surfaced —
      // as its `unattributedIssues` alert — so the badge matches what the step
      // actually shows.
      [SubmissionStepType.PrepareDocument]: (submissionIssues ?? []).filter(
        (issue) => issue.key === "documents"
      ),
      [SubmissionStepType.ReviewerPreparation]: (submissionIssues ?? []).filter(
        (issue) => issue.key !== "documents"
      ),
    };
  }, [
    issues,
    stateFields,
    facilityState,
    wasteActivityIssues,
    notificationIssues,
    submissionIssues,
  ]);
}

/**
 * Combines the backend-supplied `stepperSteps` (already ordered and filtered to
 * applicable steps) with the grouped compliance issues to produce a display
 * state per step. Only steps present in `stepperSteps` are included.
 *
 * REVIEW is special-cased on `submissionStatus` in both directions: a
 * submission only reaches PROCESSING_SUBMISSION / FILING_COMPLETE through the
 * final review approval, so the step is definitionally done once frozen — and
 * conversely it is NOT done until then, since review means every reviewer has
 * approved, not that the reviews were sent. Its persisted SubmissionStep row
 * can't be trusted in either direction: the row is only written by client-side
 * touches (so it stays NOT_STARTED when the transition happened elsewhere),
 * and pre-existing rows may read COMPLETED from the old send-time touch.
 */
export function useSubmissionStepStates(
  stepperSteps: Pick<SubmissionStepInfo, "step" | "status">[],
  facilityId: string,
  submissionStatus?: SubmissionStatus | null,
  wasteActivityIssues: Issue[] = [],
  submissionId?: string
): Partial<Record<SubmissionStepType, SubmissionStepState>> {
  const issuesByStep = useSubmissionIssuesGroupedByStep(
    facilityId,
    wasteActivityIssues,
    submissionId
  );

  return useMemo(() => {
    // Deliberately narrower than `isSubmissionFrozen`: NOT_REPORTING also
    // freezes a submission, but its review was never completed.
    const hasBeenSubmitted =
      submissionStatus === SubmissionStatus.ProcessingSubmission ||
      submissionStatus === SubmissionStatus.FilingComplete;
    const states: Partial<Record<SubmissionStepType, SubmissionStepState>> = {};
    for (const { step, status } of stepperSteps) {
      let state = getStepStatus({
        stepStatus: status,
        hasIssues: (issuesByStep[step]?.length ?? 0) > 0,
      });
      if (step === SubmissionStepType.Review) {
        if (hasBeenSubmitted) {
          state = SubmissionStepState.Done;
        } else if (state === SubmissionStepState.Done) {
          // Sent for review but not every reviewer has approved yet — the
          // submission would be frozen otherwise (the final approval is the
          // terminal action), so cap the display at InProgress.
          state = SubmissionStepState.InProgress;
        }
      }
      if (step === SubmissionStepType.SubmitAndFile) {
        // Signed still owes the filing, so only FILING_COMPLETE finishes this.
        if (submissionStatus === SubmissionStatus.FilingComplete) {
          state = SubmissionStepState.Done;
        } else if (state === SubmissionStepState.Done) {
          state = SubmissionStepState.InProgress;
        }
      }
      if (step === SubmissionStepType.ReviewAndSign) {
        // Derive from submission status: the BoldSign webhook can advance to
        // processing with no client present to touch the step row.
        if (hasBeenSubmitted) {
          state = SubmissionStepState.Done;
        } else if (
          submissionStatus === SubmissionStatus.EncampInitialReview ||
          submissionStatus === SubmissionStatus.InReview
        ) {
          // In review/signing: still in progress even if a COMPLETED row was
          // persisted, and even when no row exists yet (customer reviewer link).
          state = SubmissionStepState.InProgress;
        } else if (state === SubmissionStepState.Done) {
          state = SubmissionStepState.InProgress;
        }
      }
      states[step] = state;
    }
    return states;
  }, [stepperSteps, issuesByStep, submissionStatus]);
}
